/**
 * FieldNdviLegend — компактная легенда NDVI-палитр на карточке поля (AK-379).
 *
 * Рендерит легенду в зависимости от активного слоя:
 *   - ndvi / avg_ndvi → Палитра 1 (NDVI absolute, шкала 0.20 → 1.00 + <0.20)
 *   - ndvi_contrast   → Палитра 2 (7 относительных классов + <0.20 hard-cutoff)
 *   - gnr             → false-color, палитры нет (только пояснение)
 *   - satellite       → null (легенда не нужна)
 *
 * Палитра — из shared `@/components/map/ndviPalette` (whitelist для hex).
 * Идентична /map MapVerticalLegend, чтобы у агронома был один визуальный язык.
 */
import {
  NDVI_STOPS,
  NDVI_CONTRAST_STOPS,
  NDVI_BELOW_THRESHOLD_COLOR,
} from '@/components/map/ndviPalette';
import type { FieldRasterMode } from '@/lib/map/fieldRasterUrl';

interface Props {
  mode: FieldRasterMode;
}

export function FieldNdviLegend({ mode }: Props) {
  if (mode === 'satellite') return null;

  if (mode === 'gnr') {
    return (
      <div className="rounded-md border border-border bg-card/95 px-3 py-2 text-xs text-muted-foreground shadow-sm">
        <span className="font-medium text-foreground">GNR</span> — псевдоцвет
        (зелёный/красный/ИК). Насыщённый = активная вегетация.
      </div>
    );
  }

  if (mode === 'ndvi_contrast') {
    return (
      <div className="rounded-md border border-border bg-card/95 p-2 shadow-sm">
        <div className="mb-1 text-xs font-semibold text-foreground">
          NDVI Контрастный
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {NDVI_CONTRAST_STOPS.map((s) => (
            <div key={s.label} className="flex items-center gap-1">
              <span
                className="h-3 w-3 shrink-0 rounded-sm"
                style={{ background: s.color }}
                aria-hidden
              />
              <span className="text-xs text-muted-foreground">{s.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-1">
            <span
              className="h-3 w-3 shrink-0 rounded-sm"
              style={{ background: NDVI_BELOW_THRESHOLD_COLOR }}
              aria-hidden
            />
            <span className="text-xs text-muted-foreground">
              &lt;0.20 (почва/вода)
            </span>
          </div>
        </div>
      </div>
    );
  }

  // ndvi / avg_ndvi → Палитра 1 (горизонтальный градиент со stops).
  return (
    <div className="rounded-md border border-border bg-card/95 p-2 shadow-sm">
      <div className="mb-1 text-xs font-semibold text-foreground">
        NDVI {mode === 'avg_ndvi' ? 'средний' : ''}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {NDVI_STOPS.map((s) => (
          <div key={s.label} className="flex items-center gap-1">
            <span
              className="h-3 w-3 shrink-0 rounded-sm"
              style={{ background: s.color }}
              aria-hidden
            />
            <span className="font-mono text-xs text-muted-foreground">
              {s.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
